/**
 * hq-pack-logic
 *
 * The state behind the pack builder (snippets/hq-pack-builder.liquid), kept
 * free of the DOM so every rule can be tested in Node.
 *
 * Model
 * -----
 * The page's own shade / texture / length picker is the MAIN choice. A set is
 * a list of packs; each pack is one variant. Every option of every pack is
 * either LINKED (it follows the main choice) or CUSTOM (the shopper changed
 * that option on that pack). Changing the main choice moves only the linked
 * options, so a pack the shopper deliberately set to another shade keeps it,
 * while its length still follows if its length was never touched.
 *
 * New packs start as a copy of the main choice. Going back to a single pack
 * resets it to the main choice: with one pack there is no pack list on the
 * page, so the picker IS that pack and must show exactly what will be bought.
 *
 * Prices and the set discount are NOT computed here. They come from
 * assets/hq-bundle-pricing.js, the same arithmetic as the checkout discount.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.HqPackLogic = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SEP = '\u0000';

  function copy(values) {
    return values.slice();
  }

  function falses(n) {
    var out = [];
    for (var i = 0; i < n; i += 1) out.push(false);
    return out;
  }

  function freshPack(main) {
    return { values: copy(main), custom: falses(main.length) };
  }

  /** One pack, the main choice. `max` caps how many packs a set can hold. */
  function createState(main, max) {
    return { main: copy(main), packs: [freshPack(main)], max: Math.max(1, max || 1) };
  }

  function withPacks(state, packs) {
    return { main: state.main, packs: packs, max: state.max };
  }

  /** Grow by copies of the main choice, or shrink from the end. */
  function setCount(state, n) {
    var count = Math.max(1, Math.min(state.max, Math.floor(n) || 1));
    var packs = state.packs.slice(0, count).map(function (p) {
      return { values: copy(p.values), custom: copy(p.custom) };
    });
    while (packs.length < count) packs.push(freshPack(state.main));
    if (count === 1) packs = [freshPack(state.main)];
    return withPacks(state, packs);
  }

  function removePack(state, index) {
    if (state.packs.length <= 1 || index < 0 || index >= state.packs.length) return state;
    var packs = state.packs.filter(function (_, i) { return i !== index; });
    if (packs.length === 1) packs = [freshPack(state.main)];
    return withPacks(state, packs);
  }

  /**
   * The main picker moved. Only the positions that actually changed are
   * applied, and only to packs where that position is still linked — the
   * picker may relax several options at once (a shade with no 30" in stock
   * moves the length too), and each of those is a change like any other.
   */
  function applyMain(state, values) {
    var next = copy(values);
    var changed = [];
    for (var i = 0; i < next.length; i += 1) if (next[i] !== state.main[i]) changed.push(i);
    var packs = state.packs.map(function (p) {
      var v = copy(p.values);
      changed.forEach(function (pos) { if (!p.custom[pos]) v[pos] = next[pos]; });
      return { values: v, custom: copy(p.custom) };
    });
    return { main: next, packs: packs, max: state.max };
  }

  /**
   * One option of one pack. Choosing the main value again re-links it.
   * Refused on a single pack: that pack is edited through the main picker.
   */
  function setPackOption(state, index, pos, value) {
    if (state.packs.length < 2) return state;
    var packs = state.packs.map(function (p, i) {
      if (i !== index) return p;
      var v = copy(p.values);
      var c = copy(p.custom);
      v[pos] = value;
      c[pos] = value !== state.main[pos];
      return { values: v, custom: c };
    });
    return withPacks(state, packs);
  }

  function resetPack(state, index) {
    var packs = state.packs.map(function (p, i) { return i === index ? freshPack(state.main) : p; });
    return withPacks(state, packs);
  }

  /** True when the pack is something other than the main choice. */
  function differs(state, index) {
    var p = state.packs[index];
    for (var i = 0; i < p.values.length; i += 1) if (p.values[i] !== state.main[i]) return true;
    return false;
  }

  /* ------------------------------------------------------------------ */
  /* Variant table                                                      */
  /* ------------------------------------------------------------------ */

  function keyOf(values) {
    return values.join(SEP);
  }

  /**
   * Rows as the variant feed prints them: [id, option1, option2, option3,
   * priceCents, available, max]. `max` is the stock ceiling when Shopify
   * tracks the stock and refuses to oversell, otherwise null.
   */
  function buildTable(rows, optionCount) {
    var byKey = {};
    (rows || []).forEach(function (r) {
      if (!r || typeof r[0] !== 'number') return;
      var values = [r[1], r[2], r[3]].slice(0, optionCount).map(function (v) { return v == null ? '' : String(v).trim(); });
      byKey[keyOf(values)] = {
        id: r[0],
        values: values,
        price: Math.round(Number(r[4]) || 0),
        available: r[5] === true,
        max: typeof r[6] === 'number' ? Math.max(0, r[6]) : null,
      };
    });
    return { byKey: byKey, optionCount: optionCount };
  }

  function lookup(table, values) {
    return (table && table.byKey[keyOf(values)]) || null;
  }

  /**
   * Every pack resolved to its variant, with what stops the set being bought:
   *   missing  no variant has these values (the product does not make it)
   *   soldout  the variant exists but is unavailable
   *   short    more of this variant than is in stock; the packs past the
   *            stock ceiling are the ones marked, so the message can say
   *            how many are left
   */
  function evaluate(state, table) {
    var taken = {};
    var packs = state.packs.map(function (p, index) {
      var variant = lookup(table, p.values);
      var status = 'ok';
      if (!variant) status = 'missing';
      else if (!variant.available) status = 'soldout';
      else {
        taken[variant.id] = (taken[variant.id] || 0) + 1;
        if (variant.max != null && taken[variant.id] > variant.max) status = 'short';
      }
      return { index: index, values: p.values, variant: variant, status: status };
    });
    var problems = packs.filter(function (p) { return p.status !== 'ok'; });
    return { packs: packs, problems: problems, ready: problems.length === 0 };
  }

  /** Price the set with the checkout's own arithmetic (hq-bundle-pricing). */
  function priceOf(evaluation, tiers, pricing, step) {
    var lines = evaluation.packs
      .filter(function (p) { return p.variant; })
      .map(function (p) { return { subtotalCents: p.variant.price, quantity: 1 }; });
    return pricing.priceSet(lines, tiers, step);
  }

  /**
   * Which values of one option are buyable for a given pack: the rest of the
   * pack stays as it is and only this option changes. Used to strike out a
   * shade or length that would make the pack unbuyable.
   */
  function availability(table, values, pos, optionValues) {
    var out = {};
    optionValues.forEach(function (v) {
      var probe = copy(values);
      probe[pos] = v;
      var variant = lookup(table, probe);
      out[v] = !!(variant && variant.available);
    });
    return out;
  }

  /**
   * The /cart/add.js items. One pack is an ordinary line, exactly what the
   * plain Add to cart sent before. Two or more are a set: every line carries
   * the same _bundle_id, which is what the checkout discount groups by.
   */
  function cartItems(evaluation, opts, pricing) {
    var ids = evaluation.packs.map(function (p) { return p.variant.id; });
    if (ids.length === 1) {
      var single = [{ id: ids[0], quantity: 1 }];
      (opts.addonIds || []).forEach(function (id) { if (id) single.push({ id: id, quantity: 1 }); });
      return single;
    }
    return pricing.buildCartItems({
      variantIds: ids,
      bundleId: opts.bundleId,
      label: opts.label,
      addonIds: opts.addonIds,
    });
  }

  return {
    createState: createState,
    setCount: setCount,
    removePack: removePack,
    applyMain: applyMain,
    setPackOption: setPackOption,
    resetPack: resetPack,
    differs: differs,
    keyOf: keyOf,
    buildTable: buildTable,
    lookup: lookup,
    evaluate: evaluate,
    priceOf: priceOf,
    availability: availability,
    cartItems: cartItems,
  };
}));
